import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import PremiumUpgradeModal from '../components/PremiumUpgradeModal';

const PRIMARY = '#2DBCAF';

interface TKFeature {
  _id: string;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  isPremium?: boolean;
}

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: linear-gradient(160deg, #e8f5f3 0%, #f6f8f8 40%, #faf0e8 100%);
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 100px;
`;

const Header = styled.header`
  padding: 20px 20px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: ${PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined { font-size: 28px; color: white; }
`;

const HeaderText = styled.div`
  h1 {
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
    margin: 0;
  }
  p {
    font-size: 13px;
    color: #64748b;
    margin: 4px 0 0;
  }
`;

const ChildBanner = styled.div`
  margin: 0 16px 20px;
  padding: 16px 18px;
  background: white;
  border-radius: 16px;
  border: 2px solid rgba(45,188,175,0.15);
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ChildAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined { font-size: 26px; color: #f59e0b; }
`;

const ChildInfo = styled.div`
  flex: 1;
  h3 { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0; display: flex; align-items: center; gap: 8px; }
  p { font-size: 13px; color: #64748b; margin: 2px 0 0; }
`;

const ActiveBadge = styled.span`
  background: ${PRIMARY};
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
`;

const SwitchBtn = styled.button`
  background: #f1f5f9;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  .material-symbols-outlined { font-size: 16px; }
  &:hover { background: #e2e8f0; }
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  padding: 0 20px 12px;
  margin: 0;
`;

const FeatureList = styled.div`
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeatureCard = styled.div<{ $color?: string }>`
  background: white;
  border-radius: 18px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border: 1.5px solid #f1f5f9;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.1);
  }
  &:active { transform: scale(0.98); }
`;

const FeatureIcon = styled.div<{ $color?: string }>`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${p => p.$color || PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  .material-symbols-outlined { font-size: 28px; color: white; }
`;

const FeatureInfo = styled.div`
  flex: 1;
  min-width: 0;
  h3 {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }
  p {
    font-size: 12px;
    color: #64748b;
    margin: 4px 0 0;
    line-height: 1.4;
  }
`;

const FeatureArrow = styled.span`
  color: ${PRIMARY};
  .material-symbols-outlined { font-size: 22px; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
  .material-symbols-outlined { font-size: 48px; color: #cbd5e1; }
  h3 { font-size: 16px; font-weight: 700; color: #475569; margin: 12px 0 6px; }
  p { font-size: 13px; margin: 0; }
`;

const ComingSoonBadge = styled.span`
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
`;

const PremiumBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #FFA929 0%, #FF8C00 100%);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
  margin-left: 8px;
  
  .material-symbols-outlined {
    font-size: 12px;
    font-variation-settings: 'FILL' 1;
  }
`;

function formatAge(birthDate: string): string {
  const birth = new Date(birthDate);
  const now = new Date();
  const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (months < 12) return `${months} bulan`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return m > 0 ? `${y} tahun ${m} bulan` : `${y} tahun`;
}

// Fallback features if API fails
const DEFAULT_FEATURES: TKFeature[] = [
  { _id: '1', slug: 'stimulasi', name: 'Agenda Stimulasi', description: 'Aktivitas stimulasi harian untuk anak usia 0-6 tahun', icon: 'calendar_month', color: '#2DBCAF', isPremium: false },
  { _id: '2', slug: 'growth', name: 'Grafik Pertumbuhan', description: 'Tracking tinggi dan berat badan anak', icon: 'monitoring', color: '#3B82F6', isPremium: false },
  { _id: '3', slug: 'mpasi', name: 'Panduan MPASI', description: 'Panduan pemberian makan untuk anak usia 6 bulan - 2 tahun', icon: 'restaurant', color: '#F59E0B', isPremium: false },
  { _id: '5', slug: 'imunisasi', name: 'Checklist Imunisasi', description: 'Jadwal dan catatan imunisasi anak sesuai program pemerintah', icon: 'vaccines', color: '#EF4444', isPremium: false },
  { _id: '4', slug: 'milestone', name: 'Milestone Perkembangan', description: 'Pantau pencapaian tumbuh kembang anak', icon: 'emoji_events', color: '#8B5CF6', isPremium: false },
];

export default function TumbuhKembangPage() {
  const navigate = useNavigate();
  const { user, activeChild } = useAuth();
  const [features, setFeatures] = useState<TKFeature[]>(DEFAULT_FEATURES);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<TKFeature | null>(null);
  
  // Check if user has active premium
  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();

  // Load features from API
  useEffect(() => {
    fetch('/api/tk/features')
      .then(res => res.json())
      .then(data => {
        if (data.ok && data.features?.length > 0) {
          setFeatures(data.features);
        }
      })
      .catch(() => {});
  }, []);

  const handleFeatureClick = (feature: TKFeature) => {
    // Block premium features for non-premium users
    if (feature.isPremium && !isPremium) {
      setSelectedFeature(feature);
      setShowPremiumModal(true);
      return;
    }
    
    if (feature.slug === 'stimulasi') {
      navigate('/tumbuh-kembang/stimulasi');
    } else if (feature.slug === 'growth') {
      navigate('/tumbuh-kembang/growth');
    } else if (feature.slug === 'mpasi') {
      navigate('/tumbuh-kembang/mpasi');
    } else if (feature.slug === 'imunisasi') {
      navigate('/tumbuh-kembang/imunisasi');
    } else {
      // Coming soon
    }
  };

  return (
    <>
      <SEOMeta 
        title="Tumbuh Kembang - KinderGame.id"
        description="Pantau dan stimulasi tumbuh kembang anak dengan aktivitas yang sesuai usia"
        path="/tumbuh-kembang"
      />
      <PageWrapper>
        <Header>
          <HeaderIcon>
            <span className="material-symbols-outlined">child_care</span>
          </HeaderIcon>
          <HeaderText>
            <h1>Tumbuh Kembang</h1>
            <p>Pantau & stimulasi perkembangan anak</p>
          </HeaderText>
        </Header>

        {activeChild && (
          <ChildBanner>
            <ChildAvatar>
              <span className="material-symbols-outlined">face</span>
            </ChildAvatar>
            <ChildInfo>
              <h3>
                {activeChild.name}
                <ActiveBadge>Aktif</ActiveBadge>
              </h3>
              <p>{formatAge(activeChild.birthDate)}</p>
            </ChildInfo>
            {(user?.children?.length ?? 0) > 1 && (
              <SwitchBtn onClick={() => navigate('/profile')}>
                <span className="material-symbols-outlined">swap_horiz</span>
                Ganti
              </SwitchBtn>
            )}
          </ChildBanner>
        )}

        <SectionTitle>Fitur Tersedia</SectionTitle>
        <FeatureList>
          {features.map((feature, idx) => (
            <FeatureCard 
              key={feature._id} 
              onClick={() => handleFeatureClick(feature)}
            >
              <FeatureIcon $color={feature.color}>
                <span className="material-symbols-outlined">{feature.icon}</span>
              </FeatureIcon>
              <FeatureInfo>
                <h3>
                  {feature.name}
                  {feature.isPremium && (
                    <PremiumBadge>
                      <span className="material-symbols-outlined">crown</span>
                      Premium
                    </PremiumBadge>
                  )}
                  {feature.slug === 'milestone' && <ComingSoonBadge style={{ marginLeft: 8 }}>Segera</ComingSoonBadge>}
                </h3>
                <p>{feature.description}</p>
              </FeatureInfo>
              <FeatureArrow>
                <span className="material-symbols-outlined">chevron_right</span>
              </FeatureArrow>
            </FeatureCard>
          ))}
        </FeatureList>
      </PageWrapper>
      <BottomNavBar />
      
      {showPremiumModal && selectedFeature && (
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          featureName={selectedFeature?.name || 'fitur ini'}
        />
      )}
    </>
  );
}
